import { useEffect, useRef, useState } from "react";
import { apiClient, API } from "@/context/AuthContext";
import { getPosition, mapsUrl, uploadGeoEvidence } from "@/lib/geo";
import { Camera, Download, FileText, ImagePlus, Loader2, MapPin, Paperclip, Trash2, Upload } from "lucide-react";

export default function EvidencePanel({ finding, onToast, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/findings/${finding.id}/evidence`);
      setRows(data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [finding.id]);

  const upload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await apiClient.post(`/findings/${finding.id}/evidence`, form);
      setRows((rs) => [data, ...rs]);
      onToast?.(`Kanıt eklendi: ${data.file_name}`);
      onChanged?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Kanıt yüklenemedi");
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const captureGeo = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const geo = await getPosition();
      const data = await uploadGeoEvidence(finding.id, file, geo);
      setRows((rs) => [data, ...rs]);
      onToast?.(geo ? "Fotoğraf GPS damgasıyla eklendi" : "Fotoğraf eklendi · GPS alınamadı");
      onChanged?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Fotoğraf yüklenemedi");
    } finally { setUploading(false); ev.target.value = ""; }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/evidence/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      onToast?.("Kanıt silindi");
      onChanged?.();
    } catch { onToast?.("Kanıt silinemedi"); }
  };

  return (
    <div className="evidence-panel" data-testid={`evidence-panel-${finding.id}`}>
      <div className="evidence-head">
        <small><Paperclip size={12}/> {rows.length} kanıt dosyası</small>
        <div className="evidence-head-actions">
          <label className="secondary-btn evidence-upload" data-testid={`evidence-camera-${finding.id}`}>
            {uploading ? <Loader2 size={12} className="spin"/> : <Camera size={12}/>} Kamera + GPS
            <input type="file" accept="image/*" capture="environment" onChange={captureGeo} disabled={uploading}/>
          </label>
          <label className="secondary-btn evidence-upload" data-testid={`evidence-upload-${finding.id}`}>
            {uploading ? <Loader2 size={12} className="spin"/> : <Upload size={12}/>} Yükle
            <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.doc,.docx" onChange={upload} disabled={uploading}/>
          </label>
        </div>
      </div>
      {loading && <small>Yükleniyor…</small>}
      {!loading && rows.length === 0 && <small className="evidence-empty">Henüz kanıt yüklenmemiş. Saha fotoğrafı veya belge ekleyin.</small>}
      <div className="evidence-rows">
        {rows.map((e) => {
          const isImage = e.content_type?.startsWith("image/");
          return (
            <div key={e.id} className="evidence-row" data-testid={`evidence-${e.id}`}>
              <span className="evidence-icon">{isImage ? <ImagePlus size={14}/> : <FileText size={14}/>}</span>
              <div className="evidence-meta">
                <strong>{e.file_name}</strong>
                <small>{Math.max(1, Math.round(e.size/1024))} KB · {e.uploaded_by} · {(e.created_at||"").slice(0,16).replace("T"," ")}</small>
                {e.pin_id && <small>Pin: {e.pin_id}</small>}
                {e.geo && <a className="evidence-gps" href={mapsUrl(e.geo)} target="_blank" rel="noreferrer" data-testid={`evidence-gps-${e.id}`}><MapPin size={10}/> {e.geo.lat.toFixed(5)}, {e.geo.lng.toFixed(5)}{e.geo.accuracy ? ` ±${Math.round(e.geo.accuracy)}m` : ""}</a>}
              </div>
              <a className="secondary-btn evidence-action" href={`${API}/evidence/${e.id}/download`} target="_blank" rel="noreferrer" data-testid={`evidence-view-${e.id}`}><Download size={12}/> Aç</a>
              <button className="icon-button evidence-action" onClick={() => remove(e.id)} data-testid={`evidence-delete-${e.id}`}><Trash2 size={13}/></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
